/**
 * AdminIndependents.jsx
 * Admin moderation panel for the Independent Providers directory.
 * View pending nominations, verify (or feature), reject, delete.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Sparkles, Trash2, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminIndependents() {
  const { user, loading: authLoading } = useAuth();
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/independents/admin/pending`, { headers }),
        axios.get(`${API}/independents/stats`),
      ]);
      setPending(r1.data || []);
      setStats(r2.data || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load pending nominations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && !authLoading) refresh();
  }, [user, authLoading]);

  if (authLoading) return null;
  if (!user || !(user.is_admin || user.is_sysadmin || user.role === 'admin')) {
    return <Navigate to="/" replace />;
  }

  const act = async (id, action, opts = {}) => {
    setBusy(id);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      if (action === 'verify') await axios.post(`${API}/independents/${id}/verify`, null, { headers });
      else if (action === 'feature') await axios.post(`${API}/independents/${id}/verify?feature=true`, null, { headers });
      else if (action === 'reject') await axios.post(`${API}/independents/${id}/reject`, null, { headers });
      else if (action === 'delete') await axios.delete(`${API}/independents/${id}`, { headers });
      toast.success(`Action ${action} applied`);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(null);
    }
  };

  const seed = async () => {
    setSeeding(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/independents/admin/seed`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Seeded — ${r.data.inserted} new, ${r.data.updated} updated`);
      await refresh();
    } catch (err) {
      toast.error('Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-independents-page">
      <SEO title="Admin — Independent Providers" url="/admin/independents" noindex={true} />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold" data-testid="admin-title">Independent Providers — Moderation</h1>
            <p className="text-stone-600 mt-1">
              Review member nominations. Verify or feature the good ones.
            </p>
          </div>
          <Button onClick={seed} disabled={seeding} variant="outline" data-testid="admin-seed-btn">
            {seeding ? 'Seeding…' : 'Re-seed starter set'}
          </Button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="admin-stats">
            <Stat label="Verified+Featured" value={stats.total_verified} />
            <Stat label="Pending review" value={stats.pending_review} highlight />
            <Stat label="Stores" value={stats.by_type?.store || 0} />
            <Stat label="Vets" value={stats.by_type?.vet || 0} />
          </div>
        )}

        <h2 className="text-xl font-semibold mb-4">Pending nominations ({pending.length})</h2>

        {loading && <div className="text-stone-500" data-testid="admin-loading">Loading…</div>}

        {!loading && pending.length === 0 && (
          <Card data-testid="admin-empty">
            <CardContent className="p-8 text-center text-stone-500">
              Nothing pending. ✨
            </CardContent>
          </Card>
        )}

        <div className="space-y-3">
          {pending.map((p) => (
            <Card key={p.id} data-testid={`pending-${p.id}`}>
              <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-lg">{p.name}</h3>
                      <Badge variant="outline">{p.type}</Badge>
                      <Badge variant="outline">{p.ownership_type}</Badge>
                    </div>
                    <div className="text-sm text-stone-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                      {p.city && (<span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.city}{p.state ? `, ${p.state}` : ''}</span>)}
                      {p.founded_year && <span>est. {p.founded_year}</span>}
                      {p.website && <a href={p.website} target="_blank" rel="noopener noreferrer" className="underline truncate max-w-[20rem]">{p.website}</a>}
                    </div>
                    {p.service_notes && (
                      <p className="text-sm mt-2 text-stone-700 whitespace-pre-wrap">{p.service_notes}</p>
                    )}
                    {p.nominated_by_name && (
                      <div className="text-xs text-stone-400 mt-2">Nominated by @{p.nominated_by_name}</div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 flex-shrink-0">
                    <Button size="sm" disabled={busy === p.id} onClick={() => act(p.id, 'verify')} data-testid={`verify-${p.id}`}>
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Verify
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy === p.id} onClick={() => act(p.id, 'feature')} data-testid={`feature-${p.id}`}>
                      <Sparkles className="w-4 h-4 mr-1" /> Feature
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy === p.id} onClick={() => act(p.id, 'reject')} data-testid={`reject-${p.id}`}>
                      <XCircle className="w-4 h-4 mr-1" /> Reject
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busy === p.id} onClick={() => act(p.id, 'delete')} data-testid={`delete-${p.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`bg-white border rounded-lg px-4 py-3 ${highlight && value > 0 ? 'border-amber-300 bg-amber-50' : 'border-stone-200'}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-stone-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}
